import { FaAngleLeft, FaAngleRight } from "react-icons/fa6";
import { useTranslation } from "react-i18next";
import { css } from "../../../styled-system/css";
import { HStack, VStack } from "../../../styled-system/jsx";
import { Button } from "../core/basic/Button";
import { Select } from "../fields/base/Select";
import type { Dispatch, SetStateAction } from "react";
import { type RowData } from "@tanstack/react-table";
import { type AppTable } from "./tableFeatures";

export function RT8Pagination<TData extends RowData>({
  table,
  className,
  pageSizeOptions,
  canSetPageSize = true,
}: {
  table: AppTable<TData>;
  className?: string;
  pageSizeOptions?: number[];
  canSetPageSize?: boolean;
}) {
  const rowCount =
    table.options.rowCount && isFinite(table.options.rowCount) ?
      table.options.rowCount
    : table.getPrePaginatedRowModel().rows.length;

  return (
    <PaginationWidget
      className={className}
      totalCount={rowCount}
      currentPage={table.state.pagination.pageIndex}
      setCurrentPage={table.setPageIndex}
      pageSize={table.state.pagination.pageSize}
      setPageSize={table.setPageSize}
      canPaginateBackward={table.getCanPreviousPage()}
      canPaginateForward={table.getCanNextPage()}
      currentPageRowCount={table.getRowModel().rows.length}
      totalPages={table.getPageCount()}
      pageSizeOptions={pageSizeOptions}
      canSetPageSize={canSetPageSize}
    />
  );
}
interface Props {
  className?: string;
  totalCount?: number;
  pageSizeOptions?: number[];
  pageSize: number;
  setPageSize: (size: number) => void;
  currentPage: number;
  setCurrentPage: Dispatch<SetStateAction<number>>;
  totalPages?: number;
  canPaginateForward?: boolean;
  canPaginateBackward?: boolean;
  canSetPageSize?: boolean;
  currentPageRowCount?: number;
  hideResultsCount?: boolean;
  pageSizeLabel?: (size: number) => string;
}
export function PaginationWidget({
  className,
  totalCount,
  pageSizeOptions = [5, 10, 20, 25, 50],
  pageSize,
  setPageSize,
  currentPage,
  setCurrentPage,
  totalPages,
  canPaginateForward = true,
  canPaginateBackward = true,
  canSetPageSize = true,
  currentPageRowCount = pageSize,
  hideResultsCount,
  pageSizeLabel,
}: Props) {
  const { t } = useTranslation(["common"]);
  const hasTotalCount = totalCount != null && isFinite(totalCount);
  const hasTotalPages = totalPages != null && isFinite(totalPages);
  const previousPagesRowCount = currentPage * pageSize;
  const visibleResultsMax =
    totalCount && previousPagesRowCount + currentPageRowCount > totalCount ?
      totalCount
    : previousPagesRowCount + currentPageRowCount;
  const resolvedPageSizeLabel =
    pageSizeLabel ?? ((size: number) => t(($) => $.common.pagination["{count} rows"], { count: size }));

  const pageLabel =
    hasTotalPages ?
      t(($) => $.common.pagination["Page {current} of {total}"], { current: currentPage + 1, total: totalPages })
    : t(($) => $.common.pagination["Page {current}"], { current: currentPage + 1 });

  const resultsLabel =
    hasTotalCount ?
      t(($) => $.common.pagination["{start}-{end} of {total} results"], {
        start: previousPagesRowCount + 1,
        end: visibleResultsMax,
        total: totalCount,
        count: totalCount,
      })
    : t(($) => $.common.pagination["{start}-{end} results"], {
        start: previousPagesRowCount + 1,
        end: visibleResultsMax,
        count: visibleResultsMax,
      });

  return (
    <VStack className={className} gap="small" fontSize={"large"}>
      <HStack>
        <Button
          isDisabled={!canPaginateBackward || currentPage === 0}
          variant={"contentOnly"}
          onPress={() => setCurrentPage((currentPage) => currentPage - 1)}
        >
          <FaAngleLeft />
        </Button>
        <span className={css({ fontSize: "small" })}>{pageLabel}</span>
        {canSetPageSize ?
          <Select
            aria-label={t(($) => $.common.pagination["Page size"])}
            items={pageSizeOptions.map((opt) => ({
              label: resolvedPageSizeLabel(opt),
              value: opt,
            }))}
            selectedKey={pageSize}
            onSelectionChange={(value) => setPageSize(value as number)}
          />
        : null}
        <Button
          isDisabled={!canPaginateForward}
          variant={"contentOnly"}
          onPress={() => setCurrentPage((currentPage) => currentPage + 1)}
        >
          <FaAngleRight />
        </Button>
      </HStack>
      {!hideResultsCount && (
        <HStack>
          <span className={css({ fontSize: "small" })}>{resultsLabel}</span>
        </HStack>
      )}
    </VStack>
  );
}
